import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import MomentUtils from '@date-io/moment';

// MUI components
import { makeStyles } from '@material-ui/core/styles';
import { FormHelperText, Typography } from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  DatePicker,
  TimePicker,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Schedule } from '@material-ui/icons';

// components
import DazzlingDialog from 'components/DazzlingDialog';
import TextField from 'components/TextField';

// utils
import {
  MAX_MESSAGE_LENGTH,
  PickerMode,
  SCHEDULE_ERROR,
  getDefaultScheduleDateTime,
  getMaximumScheduleDate,
  isValidScheduleDate,
  mergeDatePart,
} from './utils';

const getInitialDraft = (scheduledAt?: string | null) =>
  scheduledAt ? moment.utc(scheduledAt).local() : getDefaultScheduleDateTime();

type ScheduleMessageDialogProps = {
  open: boolean;
  onClose: () => void;
  onSchedule: (date: Moment, body: string) => void;
  isEditing?: boolean;
  body?: string;
  scheduledAt?: string | null;
};

export default function ScheduleMessageDialog({
  open,
  onClose,
  onSchedule,
  isEditing = false,
  body,
  scheduledAt,
}: ScheduleMessageDialogProps) {
  const classes = useStyles();
  const [draft, setDraft] = useState<Moment>(() =>
    getInitialDraft(scheduledAt)
  );
  const [draftBody, setDraftBody] = useState(body ?? '');

  // Resets to the given (or a fresh default) values each time the dialog opens
  useEffect(() => {
    if (open) {
      setDraft(getInitialDraft(scheduledAt));
      setDraftBody(body ?? '');
    }
  }, [open, scheduledAt, body]);

  const handleChange = (mode: PickerMode) => (date: MaterialUiPickersDate) => {
    if (date) setDraft(current => mergeDatePart(current, date, mode));
  };

  const isDateValid = isValidScheduleDate(draft);
  const isBodyValid =
    !isEditing ||
    (!!draftBody.trim() && draftBody.length <= MAX_MESSAGE_LENGTH);
  const isValid = isDateValid && isBodyValid;

  const handleSchedule = () => {
    if (!isValid) return;
    onSchedule(draft, draftBody);
    if (!isEditing) onClose();
  };

  return (
    <DazzlingDialog
      id='scheduleMessageDialog'
      open={open}
      maxWidth='sm'
      isFullWidth
      handleClose={onClose}
      acceptLabel={isEditing ? 'Update' : 'Schedule'}
      onAccept={handleSchedule}
      disabled={!isValid}
      headerProps={{
        icon: Schedule,
        title: isEditing ? 'Update message' : 'Pick date & time',
      }}
      {...(isEditing && {
        slice: 'message',
        metaId: 'update',
        successMessage: 'Scheduled message updated successfully!',
        failureMessage: 'Scheduled message failed to update',
      })}
    >
      <>
        {isEditing && (
          <TextField
            id='scheduledMessageBody'
            placeholder='Write a message'
            multiline
            minRows={3}
            maxRows={8}
            margin='none'
            maxLength={MAX_MESSAGE_LENGTH}
            className={classes.bodyInput}
            input={{
              value: draftBody,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setDraftBody(e.target.value),
            }}
          />
        )}

        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
          <div className={classes.pickers}>
            <DatePicker
              id='scheduleMessageDate'
              className={classes.dateInput}
              autoOk
              variant='inline'
              inputVariant='outlined'
              label='Date'
              format='ddd, MMM D'
              disablePast
              maxDate={getMaximumScheduleDate()}
              value={draft}
              onChange={handleChange('date')}
            />
            <Typography>at</Typography>
            <TimePicker
              id='scheduleMessageTime'
              className={classes.timeInput}
              variant='inline'
              inputVariant='outlined'
              label='Time'
              format='h:mm A'
              value={draft}
              onChange={handleChange('time')}
            />
          </div>
        </MuiPickersUtilsProvider>

        {!isDateValid && (
          <FormHelperText error className={classes.helperText}>
            {SCHEDULE_ERROR}
          </FormHelperText>
        )}
      </>
    </DazzlingDialog>
  );
}

const useStyles = makeStyles({
  bodyInput: { marginBottom: 24 },
  pickers: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  dateInput: { width: 170 },
  timeInput: { width: 130 },
  helperText: { textAlign: 'center' },
});
